import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWorkshop } from "./context/useWorkshop";
import { SettingsProvider } from "./context/SettingsProvider";
import { useSettings } from "./context/useSettings";
import { checklistService } from "./services/checklistService";
import { createEmptyChecklist } from "./lib/models";
import { apagarRascunho } from "./lib/rascunhoLocal";
import AppShell from "./components/AppShell";
import ChecklistList from "./components/ChecklistList";
import ChecklistForm from "./components/ChecklistForm";
import ChecklistDetail from "./components/ChecklistDetail";
import DashboardPage from "./pages/DashboardPage";
import ClientesPage from "./pages/ClientesPage";
import VeiculosPage from "./pages/VeiculosPage";
import RelatoriosPage from "./pages/RelatoriosPage";
import ConfigPage from "./pages/ConfigPage";
import "./checklist.css";

// Componente principal e ponto de entrada do módulo de checklist.
// Para usar de forma independente: <ChecklistApp />
// Para integrar ao painel existente: envolva com <WorkshopProvider
// workshopId="..." workshopName="..." config={...}>, repassando os dados
// reais da oficina logada e, opcionalmente, a configuração imposta pelo
// painel central (ver README.md para detalhes de integração).
function ChecklistAppInner({ modoMobile }) {
  const { t } = useTranslation();
  const { workshopId } = useWorkshop();
  // AUD-B: segmento de inspecao da oficina (carro | moto), imposto pelo
  // painel administrativo via oficina_config_<tenant>.checklistConfig.
  // Ausente => carro, que e o comportamento historico.
  const { settings } = useSettings();

  // 'dashboard' | 'checklists' | 'clientes' | 'veiculos' | 'config'
  const [section, setSection] = useState("dashboard");
  // sub-view dentro de 'checklists': 'lista' | 'form' | 'detalhe'
  const [checklistView, setChecklistView] = useState("lista");
  const [checklistAtual, setChecklistAtual] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [checklists, setChecklists] = useState([]);
  const [erroSalvar, setErroSalvar] = useState("");
  const [sucessoSalvar, setSucessoSalvar] = useState("");

  useEffect(() => {
    checklistService.list(workshopId).then(setChecklists);
  }, [workshopId, reloadToken]);

  function refresh() {
    setReloadToken((t) => t + 1);
  }

  function abrirNovo() {
    setChecklistAtual(createEmptyChecklist(workshopId, settings.segmento));
    setChecklistView("form");
    setSection("checklists");
  }

  async function abrirExistente(id) {
    const c = await checklistService.get(workshopId, id);
    setChecklistAtual(c);
    setChecklistView("detalhe");
    setSection("checklists");
  }

  function editarAtual() {
    setChecklistView("form");
  }

  async function salvar(checklist) {
    setErroSalvar("");
    setSucessoSalvar("");
    try {
      await checklistService.save(workshopId, checklist);
      // O servidor confirmou a gravacao: a copia local perdeu a funcao.
      // Este e o UNICO ponto em que o rascunho sai automaticamente -- so
      // depois que o dado ja esta no banco.
      apagarRascunho(workshopId, checklist.id);
      refresh();
      setSucessoSalvar(
        checklist.status === "aprovado"
          ? t("checklist.sucesso_concluido")
          : t("checklist.sucesso_rascunho")
      );
      setTimeout(() => {
        setSucessoSalvar("");
        if (modoMobile) {
          // No celular, fluxo enxuto: salva e volta direto para a lista,
          // sem abrir a tela de detalhe (pensado para uso rapido em campo).
          setChecklistView("lista");
        } else {
          setChecklistAtual(checklist);
          setChecklistView("detalhe");
        }
      }, 1100);
    } catch (e) {
      setErroSalvar(e.message || t("checklist.erro_salvar"));
    }
  }

  function excluir(id) {
    if (window.confirm(t("checklist.confirm_excluir"))) {
      checklistService.remove(workshopId, id).then(refresh).catch((e) => {
        window.alert(e.message || t("checklist.erro_excluir"));
      });
    }
  }

  function navegar(nextSection) {
    setSection(nextSection);
    if (nextSection === "checklists") {
      setChecklistView("lista");
    }
  }

  return (
    <AppShell active={section} onNavigate={navegar}>
      {section === "dashboard" && (
        <DashboardPage onAbrirChecklist={abrirExistente} onNovoChecklist={abrirNovo} />
      )}

      {section === "checklists" && (
        <>
          {checklistView === "lista" && (
            <ChecklistList
              checklists={checklists}
              onOpen={abrirExistente}
              onNew={abrirNovo}
              onDelete={excluir}
            />
          )}

          {checklistView === "form" && checklistAtual && (
            <>
              {erroSalvar && (
                <div style={{
                  background: "#fef2f2", border: "1px solid #fca5a5", color: "#b91c1c",
                  borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, fontWeight: 600,
                }}>
                  {erroSalvar}
                </div>
              )}
              {sucessoSalvar && (
                <div style={{
                  background: "#f0fdf4", border: "1px solid #86efac", color: "#15803d",
                  borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, fontWeight: 600,
                }}>
                  {sucessoSalvar}
                </div>
              )}
              <ChecklistForm
                checklist={checklistAtual}
                onSave={salvar}
                onCancel={() => {
                  const jaSalvo = checklists.some((c) => c.id === checklistAtual.id);
                  setChecklistView(jaSalvo ? "detalhe" : "lista");
                }}
              />
            </>
          )}

          {checklistView === "detalhe" && checklistAtual && (
            <ChecklistDetail
              checklist={checklistAtual}
              onEdit={editarAtual}
              onBack={() => setChecklistView("lista")}
            />
          )}
        </>
      )}

      {section === "relatorios" && <RelatoriosPage onAbrirChecklist={abrirExistente} />}
      {section === "clientes" && <ClientesPage />}
      {section === "veiculos" && <VeiculosPage />}
      {section === "config" && <ConfigPage />}
    </AppShell>
  );
}

export default function ChecklistApp({ modoMobile }) {
  return (
    <SettingsProvider>
      <ChecklistAppInner modoMobile={modoMobile} />
    </SettingsProvider>
  );
}
